import { useEffect, useState } from 'react'

import { useAuth } from '@/context/AuthContext'
import { useToast } from '@/context/ToastContext'
import { useLocalStorage } from '@/hooks/utility-hooks'
import { CartItem } from '@/types/data-types'

import { useCartApi } from '@/services/m-commerce-api/cart'

import { CartActions, CartState, UseCartHookResp, UseLocalCartHookResp, UseRemoteCartHookResp } from './types'
import { getEmptyCart, reducer as localReducer } from './local-reducer'
import { reducer as remoteReducer } from './remote-reducer'
import useIntl from '@/utils/intl'
// import { calculateCart } from '@/services/m-commerce-api/cart/calculate-cart';

const useLocalCart = (): UseLocalCartHookResp => {
  const [state, setState] = useLocalStorage<CartState>('@mc_local_cart', getEmptyCart())
  // const [calcRequired, setCalcRequired] = useState<boolean>(true)
  const [loaded, setLoaded] = useState<boolean>(false)

  // const updateCart = async () => {
  //   const res = await calculateCart({
  //     localCartItems: state.items.map(item => {
  //       return {
  //         productId: item.productId,
  //         quantity: item.quantity
  //       }
  //     })
  //   })
  //   setState(res)
  //   setCalcRequired(false)
  //   setLoaded(true)
  // }
  // useEffect(() => {
  //   loadCart().catch(() => { })
  //   // updateCart()
  // }, [loaded])

  // useEffect(() => {
  //   if (calcRequired) {
  //     // updateCart()
  //     loadCart().catch(() => { })
  //   }
  // }, [calcRequired])

  useEffect(() => {
    if (!loaded) {
      loadCart().catch(() => { })
    }
  }, [])

  const loadCart = async (): Promise<void> => {
    const updatedState = await localReducer(state, { type: CartActions.FETCH_CART })
    setState(updatedState)
    setLoaded(true)
  }

  const addItem = async (item: CartItem): Promise<void> => {
    const updatedState = await localReducer(state, { type: CartActions.ADD_ITEM, payload: item })
    setState(updatedState)
    // setCalcRequired(true)
  }

  const updateItem = async (productId: number, quantity: number): Promise<void> => {
    const updatedState = await localReducer(state, { type: CartActions.UPDATE_ITEM, payload: { productId, quantity } })
    setState(updatedState)
    // setCalcRequired(true)
  }

  const removeItem = async (productId: number): Promise<void> => {
    const updatedState = await localReducer(state, { type: CartActions.REMOVE_ITEM, payload: productId })
    setState(updatedState)
    // setCalcRequired(true)
  }

  const clearCart = async (): Promise<void> => {
    const updatedState = await localReducer(state, { type: CartActions.CLEAR_CART })
    setState(updatedState)
    // setCalcRequired(true)
  }

  return {
    loaded,
    state,
    addItem,
    updateItem,
    removeItem,
    clearCart
  }
}

const useRemoteCart = (): UseRemoteCartHookResp => {
  const cartApi = useCartApi()
  const { isdn } = useAuth()
  const toast = useToast()
  const [state, setState] = useState<CartState>(getEmptyCart())
  const [loaded, setLoaded] = useState<boolean>(false)
  const { translate } = useIntl()
  useEffect(() => {
    if (isdn !== undefined) {
      fetchCart().then((cart) => {
        setLoaded(true)
      }).catch(e => {
        console.error('init remote cart failed', e)
        toast.showError(translate('request.failed'))
      })
    }
  }, [isdn])

  const addItem = async (item: CartItem): Promise<void> => {
    const updatedState = await remoteReducer(state, { type: CartActions.ADD_ITEM, payload: { cartApi, item, isdn } })
    setState(updatedState)
  }

  const updateItem = async (productId: number, quantity: number): Promise<void> => {
    const updatedState = await remoteReducer(state, { type: CartActions.UPDATE_ITEM, payload: { cartApi, productId, quantity, isdn } })
    setState(updatedState)
  }

  const removeItem = async (productId: number): Promise<void> => {
    const updatedState = await remoteReducer(state, { type: CartActions.REMOVE_ITEM, payload: { cartApi, productId, isdn } })
    setState(updatedState)
  }

  const clearCart = async (): Promise<void> => {
    const updatedState = await remoteReducer(state, { type: CartActions.CLEAR_CART, payload: { cartApi, isdn } })
    setState(updatedState)
  }

  const fetchCart = async (): Promise<void> => {
    const updatedState = await remoteReducer(state, { type: CartActions.FETCH_CART, payload: { cartApi, isdn } })
    setState(updatedState)
  }

  const mergeCart = async (
    localCartItems: Array<{
      productId: number
      quantity: number
    }>
  ): Promise<void> => {
    const updatedState = await remoteReducer(state, { type: CartActions.MERGE_CART, payload: { cartApi, isdn, req: { localCartItems } } })
    setState(updatedState)
  }

  return {
    loaded,
    state,
    addItem,
    updateItem,
    removeItem,
    clearCart,
    mergeCart
  }
}

export const useCart = (): UseCartHookResp => {
  const {
    state: remoteState,
    loaded: remoteLoaded,
    mergeCart,
    ...remoteActions
  } = useRemoteCart()

  const {
    state: localState,
    loaded: localLoaded,
    clearCart: clearLocalCart,
    ...localActions
  } = useLocalCart()

  const { isLoggedIn = false } = useAuth()
  useEffect(() => {
    if (isLoggedIn) {
      if (localState.items.length > 0) {
        mergeCart(localState.items.map(cartItem => {
          return {
            productId: cartItem.productId,
            quantity: cartItem.quantity
          }
        })).then(() => {
          clearLocalCart().catch(e => console.error('clear local cart after merge failed', e))
        }).catch(e => console.error('local cart merge failed', e))
      }
    }
  }, [isLoggedIn])

  return isLoggedIn
    ? {
        state: remoteLoaded ? remoteState : getEmptyCart(),
        loaded: remoteLoaded,
        ...remoteActions
      }
    : {
        state: localState,
        loaded: localLoaded,
        clearCart: clearLocalCart,
        ...localActions
      }
}

export { getEmptyCart } from './local-reducer'

export default useCart
